import { useState, useEffect } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { Lock, Eye, EyeOff, Check, X, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  { label: 'At least 12 characters', test: (p) => p.length >= 12 },
  { label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'At least one number', test: (p) => /[0-9]/.test(p) },
  { label: 'At least one special character (!@#$%^&*)', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) },
];

export function PasscodeStep() {
  const { data, updateData, goToNextStep, sessionToken } = useOnboarding();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const allRequirementsMet = passwordRequirements.every(req => req.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRequirementsMet && passwordsMatch && !isSaving;

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError('Please meet all password requirements');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // Determine the email/identifier to use for saving
      let saveEmail = data.email;
      if (!saveEmail && data.phoneNumber && data.countryCode) {
        const cleanPhone = `${data.countryCode}${data.phoneNumber}`.replace(/[^0-9+]/g, '');
        saveEmail = `phone_${cleanPhone}@pending.local`;
      }
      if (!saveEmail && data.accountType === 'corporate' && data.corporateCompany?.registrationNumber) {
        saveEmail = `corp_${data.corporateCompany.registrationNumber}@pending.local`;
      }

      if (!saveEmail) {
        setError('Session not found. Please restart onboarding.');
        setIsSaving(false);
        return;
      }

      // Determine current step based on account type
      const currentStepName = data.accountType === 'corporate' ? 'corporate-passcode' : 'passcode';
      const fullPhone = data.phoneNumber && data.countryCode 
        ? `${data.countryCode}${data.phoneNumber}` 
        : undefined;

      // Explicitly save the password to the database
      const { error: saveError } = await supabase.functions.invoke('save-onboarding-progress', {
        body: {
          email: saveEmail,
          current_step: currentStepName,
          account_type: data.accountType,
          onboarding_data: {
            ...data,
            passcode: password // Include the password for hashing
          },
          registration_number: data.corporateCompany?.registrationNumber,
          phone_number: fullPhone
        }
      });

      if (saveError) {
        console.error('Failed to save password:', saveError);
        setError('Failed to save password. Please try again.');
        setIsSaving(false);
        return;
      }

      // Update local state (without the actual password for security)
      updateData({ passcode: password });
      goToNextStep();
    } catch (err) {
      console.error('Error saving password:', err);
      setError('An error occurred. Please try again.');
      setIsSaving(false);
    }
  };

  // ── Already-set bypass ─────────────────────────────────────────────────────
  // passcode is set in context once saved. sessionToken means user authenticated
  // with an existing password. Either way, skip re-entry.
  const alreadySet = Boolean(data.passcode || sessionToken);

  // Auto-advance immediately — never show this step again once passcode is set
  useEffect(() => {
    if (alreadySet) {
      goToNextStep();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (alreadySet) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-24 pb-8 px-4 sm:px-6">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          <div className="mb-8">
            <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
              <Lock className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Create your password</h1>
            <p className="text-muted-foreground">
              This password will be used to resume your application if you need to continue later.
            </p>
          </div>

          {/* Already-set state card */}
          <div className="rounded-2xl border-2 border-accent/40 bg-accent/5 p-5 flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-0.5">Password Set</p>
              <p className="text-sm text-muted-foreground">Your password has already been created for this application.</p>
            </div>
          </div>

          <button
            onClick={goToNextStep}
            className="w-full h-14 rounded-full bg-accent text-accent-foreground font-semibold flex items-center justify-center gap-2 hover:bg-accent/90 transition-all mt-auto"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-8 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
            <Lock className="w-8 h-8 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Create your password
          </h1>
          <p className="text-muted-foreground">
            This password will be used to resume your application if you need to continue later.
          </p>
        </div>

        {/* Password Input */}
        <div className="space-y-4 mb-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setTouched(true);
                  setError('');
                }}
                placeholder="Enter your password"
                className="input-field w-full pr-12"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Eye className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                placeholder="Confirm your password"
                className="input-field w-full pr-12"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Eye className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-sm text-destructive flex items-center gap-1.5">
                <X className="w-4 h-4" />
                Passwords do not match
              </p>
            )}
            {passwordsMatch && (
              <p className="text-sm text-accent flex items-center gap-1.5">
                <Check className="w-4 h-4" />
                Passwords match
              </p>
            )}
          </div>
        </div>

        {/* Password Requirements */}
        <div className="rounded-2xl border border-border bg-card/50 p-4 mb-6">
          <p className="text-sm font-medium text-foreground mb-3">Password requirements:</p>
          <ul className="space-y-2">
            {passwordRequirements.map((req, index) => {
              const met = req.test(password);
              return (
                <li
                  key={index}
                  className={`text-sm flex items-center gap-2 transition-colors ${
                  touched && met
                    ? 'text-accent'
                    : 'text-muted-foreground'
                  }`}
                >
                  {touched && met ? (
                    <Check className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-current flex-shrink-0" />
                  )}
                  {req.label}
                </li>
              );
            })}
          </ul>
        </div>

        {error && (
          <p className="text-sm text-destructive mb-4">{error}</p>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full h-14 rounded-full bg-accent text-accent-foreground font-semibold flex items-center justify-center gap-2 hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all mt-auto"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            'Set Password & Continue'
          )}
        </button>

        <p className="text-xs text-muted-foreground text-center mt-4">
          Your password is encrypted and will never be shared or shown on any documents.
        </p>
      </div>
    </div>
  );
}
